import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import 'iconify-icon';
import Header from '../components/Header';
import Footer from '../components/Footer';

const programs = [
  {
    icon: 'solar:user-circle-linear',
    title: 'E-Personalidade',
    tag: 'Autoconhecimento',
    description:
      'O programa fundamental do Eneagrama: identifique seu tipo, traga consciência aos seus padrões e conquiste liberdade emocional.',
    bullets: [
      'Mapeamento dos 9 tipos do Eneagrama',
      'Identificação do seu Tipo Base, Asa e Setas',
      'Plano de desenvolvimento pessoal individualizado',
    ],
  },
  {
    icon: 'solar:body-linear',
    title: 'E-Corpo',
    tag: 'Integração Corpo-Emoção',
    description:
      'Baseado em Wilhelm Reich, descubra como suas couraças musculares revelam padrões emocionais e como liberá-las.',
    bullets: [
      'Leitura corporal e energética',
      'Liberação de couraças musculares',
      'Reconexão corpo–emoção–mente',
    ],
  },
  {
    icon: 'solar:medal-ribbons-star-linear',
    title: 'E-Got',
    tag: 'Aprofundamento',
    description:
      'Programa de imersão para quem já fez E-Personalidade e quer dar o próximo passo: integrar o Eneagrama ao seu propósito de vida.',
    bullets: [
      'Vivência intensiva e prática',
      'Trabalho com asas e setas',
      'Integração propósito + tipo',
    ],
  },
  {
    icon: 'solar:heart-linear',
    title: 'E-Relacionamentos',
    tag: 'Vida Afetiva',
    description:
      'Compreenda como os 9 tipos se relacionam, evite conflitos e construa vínculos mais profundos com quem você ama.',
    bullets: [
      'Combinações afetivas entre tipos',
      'Padrões de comunicação por perfil',
      'Estratégias para vínculos saudáveis',
    ],
  },
  {
    icon: 'solar:meditation-linear',
    title: 'E-Espiritualidade',
    tag: 'Propósito',
    description:
      'Conecte seu tipo de personalidade à sua jornada de propósito, expansão de consciência e desenvolvimento espiritual.',
    bullets: [
      'Caminhos espirituais por tipo',
      'Práticas contemplativas',
      'Vida com propósito',
    ],
  },
];

const SolutionsIndividual = () => {
  const { t } = useTranslation();
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 },
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div data-testid="solutions-individual-page">
      <Header />
      <main className="pt-20">
        <section className="py-24 border-b border-neutral-900 relative overflow-hidden">
          <div className="absolute inset-0 bg-grid opacity-30"></div>
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-gray-400 mb-4 block animate-on-scroll">
                {t('pages.solutions_individual.hero_label')}
              </span>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 animate-on-scroll delay-100">
                {t('pages.solutions_individual.title_part1')} <span className="text-accent-orange">{t('pages.solutions_individual.title_highlight')}</span>
              </h1>
              <p className="text-lg text-neutral-400 mb-8 leading-relaxed animate-on-scroll delay-200">
                {t('pages.solutions_individual.hero_subtitle')}
              </p>
              <div className="flex flex-wrap justify-center gap-4 animate-on-scroll delay-300">
                <Link to="/agenda" className="beam-btn group" data-testid="solutions-individual-agenda-btn">
                  <div className="beam-spinner"></div>
                  <div className="beam-btn-content">
                    <span>{t('pages.solutions_individual.see_classes')}</span>
                    <iconify-icon icon="solar:calendar-linear"></iconify-icon>
                  </div>
                </Link>
                <Link to="/contato" className="px-8 py-4 text-sm font-mono uppercase tracking-wider text-neutral-400 hover:text-[#EAEAEA] transition-colors">
                  {t('pages.solutions_individual.talk_consultant')}
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 relative" style={{ background: "rgba(10, 10, 10, 0.72)" }}>
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4 animate-on-scroll">{t('pages.solutions_individual.programs_title')}</h2>
              <p className="text-lg text-neutral-400 animate-on-scroll delay-100">
                {t('pages.solutions_individual.programs_subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {programs.map((p, i) => (
                <div
                  key={i}
                  className="glow-card bg-[#1A1A1A] border border-[#333333] p-8 rounded-xl group cursor-pointer animate-on-scroll"
                  style={{ transitionDelay: `${i * 0.1}s` }}
                  data-testid={`solutions-individual-card-${i}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 rounded-full bg-[#1F1F1F] flex items-center justify-center group-hover:scale-110 transition-transform">
                      <iconify-icon icon={p.icon} className="text-3xl"></iconify-icon>
                    </div>
                    <span className="text-xs font-mono text-neutral-400 uppercase tracking-widest">{p.tag}</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-semibold mb-3 tracking-tight">{p.title}</h3>
                  <p className="text-sm text-neutral-400 mb-5 leading-relaxed">{p.description}</p>
                  <ul className="space-y-2 mb-6">
                    {p.bullets.map((b, k) => (
                      <li key={k} className="flex items-start gap-2 text-sm text-neutral-700">
                        <iconify-icon icon="solar:check-circle-linear" className="text-base mt-0.5 flex-shrink-0"></iconify-icon>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contato"
                    className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider hover:gap-3 transition-all"
                  >
                    {t('pages.solutions_individual.interested')}
                    <iconify-icon icon="solar:arrow-right-linear"></iconify-icon>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="relative py-24 text-white" style={{ background: 'rgba(10, 10, 10, 0.78)' }}>
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">{t('pages.solutions_individual.cta_title')}</h2>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              {t('pages.solutions_individual.cta_subtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/equipe" className="px-8 py-4 bg-accent-orange text-white rounded-full text-sm font-mono uppercase tracking-wider hover:bg-[#E55D00] transition-colors">
                {t('pages.solutions_individual.find_consultant')}
              </Link>
              <Link to="/agenda" className="px-8 py-4 border border-[#333333] text-[#EAEAEA] rounded-full text-sm font-mono uppercase tracking-wider hover:border-accent-orange hover:text-accent-orange transition-colors">
                {t('pages.solutions_individual.see_agenda')}
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default SolutionsIndividual;
